import {Theme} from "@mui/material/styles";
import {alpha, keyframes} from "@mui/system";

/**
 * The folio dialog's width - and the digital signing details dialog's, which opens as a view of it:
 * the deprecated Folios dialog's, which its table sized (1000px of columns, 24px padding each side).
 * On a smaller screen the dialog's `calc(100% - 64px)` narrows it.
 */
export const FOLIO_DIALOG_MAX_WIDTH = 1048;

/**
 * The folio dialog's least height, in both its views - not the digital signing details dialog's.
 * A folio with a line or two (373px, 413px) and the list of a folio or two (about 300px) all open at
 * it, so switching between them doesn't move the dialog; more content grows it as before.
 */
export const FOLIO_DIALOG_MIN_HEIGHT = 420;

/** A small label over a block of the folio view (its payment): small caps, never below 12px. */
export const FOLIO_LABEL_SX = {
    m: 0,
    mb: 1,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
};

/**
 * Between the fields of a finalized folio's payment - the payment method, its provider's compact form:
 * an outlined field's floating label overhangs its box by about 8px, so 16px leaves the same 8px above
 * every label.
 */
export const FOLIO_FIELD_GAP = 2;

/** A note ("No payment providers available"): plain secondary text - an Alert is for warnings. */
export const FOLIO_NOTE_SX = {fontSize: 13, lineHeight: '18px', color: 'text.secondary'};

/**
 * A warning, an error or a status in the folio view and its dialogs - the payment forms, digital
 * signing: an Alert at 13px, without the tall padding.
 */
export const FOLIO_ALERT_SX = {
    px: 1.5,
    py: 0,
    fontSize: 13,
    lineHeight: '18px',
    '& .MuiAlert-icon': {py: '8px', mr: 1, fontSize: 18},
    '& .MuiAlert-message': {py: '8px'},
    '& .MuiAlert-action': {pt: '2px', pl: 1},
};

const arrivalFade = (tint: string) => keyframes`
    0%, 25% { background-color: ${tint}; }
    100% { background-color: transparent; }
`;

/**
 * What arrives from the server while the folio shows - a line, e.g. the tip a signing
 * posts, and the totals it changes: a soft tint that fades out, once. Nothing moves with reduced
 * motion.
 */
export const folioArrivalSx = (theme: Theme) => ({
    animation: `${arrivalFade(alpha(theme.palette.success.main, 0.16))} 2s ease-out`,
    '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
});
